import { ReactElement, useState, useRef, useMemo } from 'react';
import {
  Leader, StandardLeaderPermissions, OrganizationLeader,
  CategoryLeaderPermissions, SchedulingPermission
} from '../../store';
import { getLeaderPermission } from '../../store/templates';
import SharedLeaderItem from './SharedLeaderItem';
import { CabButton, CabAutocomplete, CabTabs } from '@CabComponents';
import { CabModal } from '@CabComponents/CabModal';
import { 
  Box, FormLabel, Snackbar, Typography
} from '@mui/material';
import LeaderEmailsModal from '../Schedule/LeaderEmailsModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { OrganizationUserAutocompleteOption } from '../../store/cabinetApi/generated/core';
import { SharedLeaderGrant } from '../../store/cabinetApi/generated/appCore';
import { ProfileCategory } from '../../store/cabinetApi/generated/profiles';
import { PermissionsFormData } from './PermissionsForm';
import PermissionsForm from './PermissionsForm';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';


interface Props {
  leader: Leader | OrganizationLeader;
  isOpen: boolean;
  onDidDismiss: () => void;
  sharedLeaderGrants: SharedLeaderGrant[];
  shareWithUserOptions: OrganizationUserAutocompleteOption[];
  leaderProfileCategories: Record<number, ProfileCategory>;
  onShare: (userIds: OrganizationUserAutocompleteOption['value'][], 
    standardPermissions: StandardLeaderPermissions,
    categoryPermissions: CategoryLeaderPermissions,
    schedulingPermissions: SchedulingPermission,
    crmAllowAllContacts: boolean,
    startDate: string, endDate: string | null, isAdmin: boolean,
  ) => Promise<void>;
  onRevoke: (grantId: SharedLeaderGrant['id'], leaderId: Leader['id']) => void;
  onUpdateLeaderEmails: (leaders: Leader[]) => Promise<void>;
  onEditPermissions?: (grantId: SharedLeaderGrant['id'], permissionsData: PermissionsFormData) => Promise<void>;
}


interface ShareGrantForm {
  collaborators: OrganizationUserAutocompleteOption['value'][];
  startDate: string;
  endDate: string | null;
  schedulingPermissionEdit: boolean;
  schedulingPermissionAddToMeetings: boolean;
  crmAllowAllContacts: boolean;
  standardPermissions: StandardLeaderPermissions;
  customCategoryPermissions: CategoryLeaderPermissions;
  permissionType: 'all' | 'limited';
}


export const ShareLeader = ({
  leader, isOpen, onDidDismiss, sharedLeaderGrants, shareWithUserOptions, leaderProfileCategories, onShare, 
  onRevoke, onUpdateLeaderEmails, onEditPermissions
}: Props): ReactElement => {
  const topOfViewRef = useRef<HTMLDivElement>(null);
  const primaryAssistantId = typeof(leader.primary_assistant) === 'number' ? leader.primary_assistant 
    : leader.primary_assistant?.id || -1;

  const [shareConfirmationMessage, setShareConfirmationMessage] = useState<null | string>(null);
  const [activeTab, setActiveTab] = useState(0);

  const defaultValues = useMemo(() => ({
    collaborators: [],
    startDate: DateTime.now().toISO(),
    endDate: null,
    schedulingPermissionEdit: false,
    schedulingPermissionAddToMeetings: true,
    crmAllowAllContacts: false,
    standardPermissions: {
      basic: getLeaderPermission(),
      airlines: getLeaderPermission(),
      hotels: getLeaderPermission(),
      personal: getLeaderPermission(),
    },
    customCategoryPermissions: Object.values(leaderProfileCategories)
      .map(c => ({ [c.id]: getLeaderPermission() }))
      .reduce((prev, cur) => ({ ...prev, ...cur }), {}),
    permissionType: 'all' as const,
  }), [leaderProfileCategories]);

  const { control, formState, setValue, handleSubmit, reset, watch } = useForm<ShareGrantForm>({ 
    values: defaultValues 
  });

  const handleShareProfile: SubmitHandler<ShareGrantForm> = async (data) => {
    const shareMessage = `${leader.first_name} ${leader.last_name} has been successfully shared with 
    ${shareWithUserOptions.filter(u => data.collaborators.includes(u.value))
      .map(c => c.label.replace(/\([^()]*\)/g, '').trim())
      .join(', ')}`;

    const isAdmin = data.permissionType === 'all';
    await onShare(
      data.collaborators,
      data.standardPermissions,
      data.customCategoryPermissions,
      {
        create: data.schedulingPermissionEdit,
        view: data.schedulingPermissionEdit,
        edit: data.schedulingPermissionEdit,
        delete: data.schedulingPermissionEdit,
        add_to_meeting: data.schedulingPermissionAddToMeetings,
      },
      isAdmin || data.crmAllowAllContacts,
      data.startDate,
      data.endDate || null,
      isAdmin,
    );

    topOfViewRef.current?.scrollIntoView({block: 'end', inline: 'nearest', behavior: 'smooth'});
    setShareConfirmationMessage(shareMessage);
    reset();
    onDidDismiss();
  };

  const handlePermissionsFormSubmit = (permissionsData: PermissionsFormData) => {
    // Update the form with the permissions data
    setValue('startDate', permissionsData.startDate);
    setValue('endDate', permissionsData.endDate);
    setValue('schedulingPermissionEdit', permissionsData.schedulingPermissionEdit);
    setValue('schedulingPermissionAddToMeetings', permissionsData.schedulingPermissionAddToMeetings);
    setValue('crmAllowAllContacts', permissionsData.crmAllowAllContacts);
    setValue('standardPermissions', permissionsData.standardPermissions);
    setValue('customCategoryPermissions', permissionsData.customCategoryPermissions);
    setValue('permissionType', permissionsData.permissionType);
    
    // Submit the form
    handleSubmit(handleShareProfile)();
  };

  return (
    <>
      <CabModal
        open={isOpen}
        onClose={onDidDismiss}
        title={`Share ${leader.first_name}`}
        closeIcon
        closeOnBackdropClick
        actionButtons={
          activeTab === 0 ? (
            <CabButton 
              onClick={handleSubmit(handleShareProfile)} 
              disabled={!formState.isValid || formState.isLoading || formState.isSubmitting}
            >
              Share
            </CabButton>
          ) : undefined
        }
        sx={{ '& .MuiPaper-root': { width: 600, height: 900 } }}
      >
        <CabTabs
          tabs={[
            { label: 'Invite User' },
            { label: 'Current Collaborators' }
          ]}
          initValue={activeTab}
          onChangeTab={setActiveTab}
          tabPanelSx={{ 
            height: 'calc(100% - 48px)', 
            overflow: 'auto',
            padding: 0
          }}
        >
          {/* Invite User Tab */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: 2
            }}>
            <Box>
              <Controller name="collaborators" control={control} render={({ field: { ref, ...field }}) => (
                <>
                  <FormLabel>
                    <Typography variant='body1'>Share Teammate with:</Typography>
                  </FormLabel>
                  <CabAutocomplete<number, never>
                    onChange={(v) => Array.isArray(v) && field.onChange(v ? v : null)}
                    multiple
                    clearable
                    placeholder={field.value.length < 1 ? 'Find users by name or email' : undefined}
                    options={shareWithUserOptions.filter(o => o.value !== primaryAssistantId)}
                  />
                </>
              )} />
            </Box>

            <PermissionsForm
              leaderProfileCategories={leaderProfileCategories}
              showUserSelector={false}
              showDateRange={true}
              parentControl={control as unknown as Control<PermissionsFormData>}
              parentSetValue={setValue as unknown as UseFormSetValue<PermissionsFormData>}
              parentWatch={watch as unknown as UseFormWatch<PermissionsFormData>}
              onSubmit={handlePermissionsFormSubmit}
              onCancel={() => {}}
            />
          </Box>

          {/* Current Collaborators Tab */}
          <Box sx={{ padding: 2 }}>
            {sharedLeaderGrants.map((grant) => {
              return (
                <SharedLeaderItem key={grant.id} grant={grant} leader={leader}
                  categories={leaderProfileCategories}
                  revoke={onRevoke}
                  onEditPermissions={onEditPermissions}
                />
              );
            })}

            {sharedLeaderGrants.length === 0 && (
              <Typography
                sx={{
                  marginTop: 1,
                  fontWeight: "bold"
                }}>
                None of your teammates currently have access to this profile
              </Typography>
            )}
          </Box>
        </CabTabs>
      </CabModal>
      <Snackbar
        open={!!shareConfirmationMessage}
        onClose={() => setShareConfirmationMessage(null)}
        autoHideDuration={8000}
        message={shareConfirmationMessage}
        sx={{ marginTop: '96px' }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      />
      {isOpen && !leader.email && "is_shared" in leader && (
        <LeaderEmailsModal
          isOpen={isOpen && !leader.email}
          leaders={[leader]}
          onEmailsSubmitted={onUpdateLeaderEmails}
          onCancel={onDidDismiss}
        />
      )}
    </>
  );
};

export default ShareLeader;
